import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AskRichardWordmark } from '~/components/brand';

import type { LensPanelMode } from './panelTypes';
import { SidebarHeaderSlot } from './sidebarHeaderSlot';

export type LensSidebarProps = {
  mode: LensPanelMode;
  onModeChange: (mode: LensPanelMode) => void;
  onClose: () => void;
  chat?: React.ReactNode;
  chatBanner?: React.ReactNode;
  allowCardEditing?: boolean;
  cardSelected: boolean;
  cardEditorOpen: boolean;
  lensContent?: React.ReactNode;
  /** Read vs build: outside edit mode only askRichard is offered; Card and Lens appear with edit mode. */
  hideEditTabs?: boolean;
};

/**
 * The Lens page's one right-hand sidebar: askRichard first, then the selected card's manual editor
 * (rendered by the page beneath this header while `cardEditorOpen`) and the Lens settings. The
 * askRichard / Card / Lens toggle spans the header; outside edit mode only askRichard is offered,
 * and Card and Lens join it when the board enters edit mode. The chat stays mounted while
 * a tab or the selected card changes, so an answer in progress keeps streaming.
 */
export default function LensSidebar({
  mode,
  onModeChange,
  onClose,
  chat,
  chatBanner,
  allowCardEditing = true,
  cardSelected,
  cardEditorOpen,
  lensContent,
  hideEditTabs = false,
}: LensSidebarProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const shownMode: LensPanelMode = cardEditorOpen ? 'edit' : mode === 'lens' && lensContent ? 'lens' : 'ask';
  const chatOwnsHeader = Boolean(chat);
  const chatHidden = shownMode !== 'ask';

  const collapse = (
    <Tooltip title="Collapse">
      <IconButton aria-label="Close askRichard panel" onClick={onClose} size="small">
        <KeyboardDoubleArrowRightIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const showCardTab = allowCardEditing && !hideEditTabs;
  const showLensTab = Boolean(lensContent) && !hideEditTabs;
  const leading = (
    <>
      {collapse}
      {chat || showCardTab || showLensTab ? (
        <ToggleButtonGroup
          aria-label="Pane mode"
          exclusive
          onChange={(_event, next: LensPanelMode | null) => {
            if (next) {
              onModeChange(next);
            }
          }}
          size="small"
          sx={{
            border: 1,
            borderColor: tokens.accent,
            borderRadius: 1,
            flex: 1,
            height: 30,
            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              borderRadius: 1,
              flex: 1,
              fontWeight: 600,
              textTransform: 'none',
              '&.Mui-selected': {
                bgcolor: tokens.accent,
                color: tokens.accentContrast,
                '&:hover': { bgcolor: tokens.accent },
              },
            },
          }}
          value={shownMode}
        >
          {chat ? (
            <ToggleButton aria-label="Ask with askRichard" value="ask">
              <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}>
                <AutoAwesomeOutlinedIcon aria-hidden="true" fontSize="small" />
                <AskRichardWordmark
                  inverted={shownMode === 'ask' ? theme.palette.mode === 'light' : theme.palette.mode === 'dark'}
                  size={13}
                />
              </Box>
            </ToggleButton>
          ) : null}
          {showCardTab ? (
            <Tooltip disableHoverListener={cardSelected} title="Select a card to edit it">
              <Box component="span" sx={{ display: 'flex', flex: 1 }}>
                <ToggleButton disabled={!cardSelected} value="edit">
                  Card
                </ToggleButton>
              </Box>
            </Tooltip>
          ) : null}
          {showLensTab ? <ToggleButton value="lens">Lens</ToggleButton> : null}
        </ToggleButtonGroup>
      ) : null}
    </>
  );

  return (
    <Box
      aria-label="askRichard conversation"
      component="aside"
      sx={{
        display: 'flex',
        flex: shownMode === 'edit' ? '0 0 auto' : 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      {chatOwnsHeader ? null : (
        <Stack
          alignItems="center"
          direction="row"
          spacing={1}
          sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0, px: 2, py: 1 }}
        >
          {leading}
        </Stack>
      )}
      {chat ? (
        <Box sx={{ display: 'flex', flex: chatHidden ? '0 0 auto' : 1, flexDirection: 'column', minHeight: 0 }}>
          <SidebarHeaderSlot.Provider
            value={{
              leading,
              banner: !chatHidden && chatBanner ? <Box sx={{ flexShrink: 0, px: 2, py: 1 }}>{chatBanner}</Box> : null,
              chatHidden,
              hideCardScope: cardEditorOpen,
              onShowChat: () => onModeChange('ask'),
            }}
          >
            {chat}
          </SidebarHeaderSlot.Provider>
        </Box>
      ) : null}
      {shownMode === 'lens' ? (
        <Box aria-label="Lens settings" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {lensContent}
        </Box>
      ) : null}
    </Box>
  );
}
